import { useEffect, useReducer, useRef } from 'react'
import { subscribeSharedSse, type SharedSseInfo, type SharedSseState } from './sharedSse'
import type { InputView, OutputView, SampleEvent, UserView } from './api'

// Live data over SSE (/api/events): a snapshot, then changed inputs and
// outputs. Values that change every second (bitrate, lag, fps, jitter, byte
// counters, the SRT block of an SRT input) do not trigger input/output
// events: they come in the sample event every 5 s. EventSource reconnects by itself; we show that state.
// limited: the center refused the stream by its per-user limit (429, decision 51); tried again after Retry-After.

export type Conn = 'connecting' | 'open' | 'reconnecting' | 'limited'

interface State {
  conn: Conn
  view: UserView | null
  /** when the last snapshot or sample arrived (ms): byte counters grow from here */
  at: number
  /** limited: the center's limit and the user's open streams, when it says */
  limit?: SharedSseInfo
}

const KEEP = 60 // samples per object: 5 minutes

function append<T>(rows: T[] | undefined, x: T): T[] {
  const r = rows ? [...rows, x] : [x]
  return r.length > KEEP ? r.slice(r.length - KEEP) : r
}

type Action =
  | { t: 'conn'; conn: Conn; limit?: SharedSseInfo }
  | { t: 'snapshot'; view: UserView }
  | { t: 'input'; input: InputView }
  | { t: 'output'; output: OutputView }
  | { t: 'output_removed'; id: string }
  | { t: 'node'; node: UserView['node'] }
  | { t: 'sample'; sample: SampleEvent }

function reduce(s: State, a: Action): State {
  if (a.t === 'conn') return { ...s, conn: a.conn, limit: a.limit }
  if (a.t === 'snapshot') return { ...s, view: a.view, at: Date.now() }
  if (!s.view) return s
  const v = s.view
  switch (a.t) {
    case 'input': {
      const inputs = v.inputs.some((i) => i.stream_name === a.input.stream_name)
        ? v.inputs.map((i) => (i.stream_name === a.input.stream_name ? a.input : i))
        : [...v.inputs, a.input]
      return { ...s, view: { ...v, inputs } }
    }
    case 'output': {
      const outputs = v.outputs.some((o) => o.output_id === a.output.output_id)
        ? v.outputs.map((o) => (o.output_id === a.output.output_id ? a.output : o))
        : [...v.outputs, a.output].sort((x, y) => x.destination_id - y.destination_id)
      return { ...s, view: { ...v, outputs } }
    }
    case 'output_removed':
      return { ...s, view: { ...v, outputs: v.outputs.filter((o) => o.output_id !== a.id) } }
    case 'node':
      return { ...s, view: { ...v, node: a.node } }
    case 'sample': {
      const so = a.sample.outputs
      const si = a.sample.inputs
      const outputs = v.outputs.map((o) => {
        const x = so[o.output_id]
        if (!x) return o
        // Delay numbers change without output events (docs/m15-live-stats-ui-handoff.md 2): the sample carries
        // delay.total_ms as delay_ms; kind and warning stay as the last output event set them.
        const delay = o.delay && o.delay.kind !== 'none' && x.delay_ms !== undefined ? { ...o.delay, total_ms: x.delay_ms } : o.delay
        // Viewers too: the platform block and the map label follow sample.outputs[id].viewers.
        const platform = o.platform?.stats && x.viewers !== undefined ? { ...o.platform, stats: { ...o.platform.stats, viewers: x.viewers } } : o.platform
        // fill_kbps: not in the contract's sample yet; when a server sends it, the share follows every 5 s,
        // otherwise the last output event's value stays (bitrate_kbps here already includes the filler).
        return { ...o, bitrate_kbps: x.bitrate_kbps, lag_ms: x.lag_ms, bytes_sent: x.bytes_sent ?? o.bytes_sent, delay, platform, fill_kbps: x.fill_kbps ?? o.fill_kbps }
      })
      const inputs = v.inputs.map((i) => {
        const x = si[i.stream_name]
        return x
          ? {
              ...i, bitrate_kbps: x.bitrate_kbps, fps: x.fps ?? i.fps, filler_kbps: x.filler_kbps ?? 0, jitter_ms: x.jitter_ms ?? i.jitter_ms, bytes_received: x.bytes_received ?? i.bytes_received,
              // delay_ms of an input is delay.to_us_ms; RTT and backlog come with input events only
              delay: i.delay && x.delay_ms !== undefined ? { ...i.delay, to_us_ms: x.delay_ms } : i.delay,
              // The SRT block (RTT, loss, resends…) changes without input events: the sample carries it every 5 s.
              network: x.network?.srt ? { ...i.network, srt: x.network.srt } : i.network,
            }
          : i
      })
      // The history series grows by the window that just closed; with no
      // server history yet (old API) this is the client-side buffer.
      const h = v.history ?? { interval_s: 5, outputs: {}, inputs: {} }
      const ho = { ...h.outputs }
      for (const [id, x] of Object.entries(so)) ho[id] = append(ho[id], { ...x, fill_kbps: undefined, t: x.t ?? a.sample.t })
      const hi = { ...h.inputs }
      for (const [name, x] of Object.entries(si)) {
        // the history keeps chart fields only, as the server's ring
        hi[name] = append(hi[name], { ...x, network: undefined, t: x.t ?? a.sample.t })
      }
      // Watching in the summary: top-level fields of the sample (absent = no platform reports viewers).
      const viewers = 'viewers_total' in a.sample || v.viewers_total !== undefined
        ? { viewers_total: a.sample.viewers_total, viewers_delta_5m: a.sample.viewers_delta_5m }
        : {}
      return { ...s, at: Date.now(), view: { ...v, ...viewers, outputs, inputs, history: { ...h, outputs: ho, inputs: hi } } }
    }
  }
}

const EVENTS_PATH = '/api/events'
// me.changed (design §6): the plan or its limits changed; GET /api/me is read again. Not sent by
// the center yet (BILL-B3): until then the session also reads /api/me once a minute.
const EVENT_NAMES = ['snapshot', 'input', 'output', 'output_removed', 'node', 'sample', 'me.changed'] as const

/** One event of the stream as the reducer's action. */
function toAction(name: string, data: string): Action | null {
  const d = JSON.parse(data) as unknown
  switch (name) {
    case 'snapshot':
      return { t: 'snapshot', view: d as UserView }
    case 'input':
      return { t: 'input', input: d as InputView }
    case 'output':
      return { t: 'output', output: d as OutputView }
    case 'output_removed':
      return { t: 'output_removed', id: (d as { output_id: string }).output_id }
    case 'node':
      return { t: 'node', node: (d as { node: UserView['node'] }).node }
    case 'sample':
      return { t: 'sample', sample: d as SampleEvent }
  }
  return null
}

/** The shared stream's state as the cabinet shows it (paused: every tab hidden, nobody looks). */
function connOf(s: SharedSseState): Conn {
  return s === 'paused' ? 'connecting' : s
}

/**
 * Live data of the signed-in user. The tabs of this origin share one stream
 * (sharedSse.ts): the leader tab holds it, the others get its events and, on
 * joining, its current view as a snapshot.
 */
export function useLive(enabled: boolean, onMeChanged?: () => void) {
  const [state, dispatch] = useReducer(reduce, { conn: 'connecting', view: null, at: 0 })
  const view = useRef<UserView | null>(null)
  view.current = state.view
  const meChanged = useRef(onMeChanged)
  meChanged.current = onMeChanged
  useEffect(() => {
    if (!enabled) return
    return subscribeSharedSse({
      path: EVENTS_PATH,
      key: 'strevix-live-events',
      events: EVENT_NAMES,
      onEvent: (e) => {
        if (e.name === 'me.changed') return meChanged.current?.()
        const a = toAction(e.name, e.data)
        if (a) dispatch(a)
      },
      onState: (s, info) => dispatch({ t: 'conn', conn: connOf(s), limit: s === 'limited' ? info : undefined }),
      snapshot: () => (view.current ? { name: 'snapshot', data: JSON.stringify(view.current) } : null),
    })
  }, [enabled])
  return state
}
